import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, SkipForward, ArrowLeft, RotateCcw, ListMusic, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { waShareLink } from "../lib/store";
import { ALL_STORIES, findStory } from "../data/regalStories";

const COLLECTION_POSES = {
  "gold-history": "point",
  emotional: "welcome",
  educational: "idea",
  "customer-journey": "arms",
};

function splitSentences(script) {
  return (script.match(/[^.!?]+[.!?]+/g) || [script]).map((s) => s.trim());
}

function markPlayed(slug) {
  try {
    const arr = JSON.parse(localStorage.getItem("rj_stories_played") || "[]");
    if (!arr.includes(slug)) localStorage.setItem("rj_stories_played", JSON.stringify([...arr, slug]));
  } catch (e) {
    console.debug("played save failed:", e?.message);
  }
}

export default function StoryPlayer() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const story = findStory(slug);
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [soundOn, setSoundOn] = useState(() => sessionStorage.getItem("rw_sound") === "1");
  const [segIdx, setSegIdx] = useState(-1);
  const [progress, setProgress] = useState(0);
  const [autoplay, setAutoplay] = useState(() => localStorage.getItem("rj_story_autoplay") !== "0");
  const [countdown, setCountdown] = useState(null);
  const chainTimers = useRef([]);

  const clearChain = () => {
    chainTimers.current.forEach(clearTimeout);
    chainTimers.current = [];
    setCountdown(null);
  };

  const sentences = useMemo(() => (story ? splitSentences(story.script) : []), [story]);
  const weights = useMemo(() => {
    const total = sentences.reduce((a, s) => a + s.length, 0);
    let acc = 0;
    return sentences.map((s) => (acc += s.length / total));
  }, [sentences]);

  const idx = ALL_STORIES.findIndex((s) => s.slug === slug);
  const next = ALL_STORIES[(idx + 1) % ALL_STORIES.length];

  useEffect(() => {
    setPlaying(false);
    setEnded(false);
    setSegIdx(-1);
    setProgress(0);
    clearChain();
    let t = null;
    if (location.state?.auto) {
      t = setTimeout(() => {
        const a = audioRef.current;
        if (!a) return;
        const sound = sessionStorage.getItem("rw_sound") === "1";
        a.muted = !sound;
        a.play().then(() => {
          setPlaying(true);
          const amb2 = ambientRef.current;
          if (amb2 && sound) { amb2.volume = 0.09; amb2.play().catch(() => {}); }
        }).catch(() => {});
      }, 500);
    }
    const amb = ambientRef.current;
    return () => {
      if (t) clearTimeout(t);
      clearChain();
      audioRef.current?.pause();
      amb?.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!story) return <Navigate to="/stories" replace />;

  const syncAmbient = (isPlaying, sound) => {
    const amb = ambientRef.current;
    if (!amb) return;
    amb.volume = 0.09;
    if (isPlaying && sound) amb.play().catch(() => {});
    else amb.pause();
  };

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
      syncAmbient(false, soundOn);
    } else {
      a.muted = !soundOn;
      setEnded(false);
      a.play().then(() => {
        setPlaying(true);
        syncAmbient(true, soundOn);
      }).catch(() => {});
    }
  };

  const toggleSound = () => {
    const nextSound = !soundOn;
    setSoundOn(nextSound);
    sessionStorage.setItem("rw_sound", nextSound ? "1" : "0");
    if (audioRef.current) audioRef.current.muted = !nextSound;
    syncAmbient(playing, nextSound);
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const frac = a.currentTime / a.duration;
    setProgress(frac * 100);
    setSegIdx(weights.findIndex((w) => frac <= w));
  };

  const onEnded = () => {
    setPlaying(false);
    setEnded(true);
    setProgress(100);
    markPlayed(slug);
    syncAmbient(false, soundOn);
    if (autoplay) {
      setCountdown(3);
      [2, 1].forEach((n, i) => chainTimers.current.push(setTimeout(() => setCountdown(n), (i + 1) * 1000)));
      chainTimers.current.push(setTimeout(() => {
        setCountdown(null);
        navigate(`/stories/${next.slug}`, { state: { auto: true } });
      }, 3000));
    }
  };

  const toggleAutoplay = () => {
    const nextAuto = !autoplay;
    setAutoplay(nextAuto);
    localStorage.setItem("rj_story_autoplay", nextAuto ? "1" : "0");
    if (!nextAuto) clearChain();
  };

  const replay = () => {
    clearChain();
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    setEnded(false);
    a.muted = !soundOn;
    a.play().then(() => { setPlaying(true); syncAmbient(true, soundOn); }).catch(() => {});
  };

  const pose = COLLECTION_POSES[story.collection] || "welcome";
  const subtitle = segIdx >= 0 && segIdx < sentences.length ? sentences[segIdx] : null;
  const storyUrl = `${window.location.origin}/stories/${story.slug}`;
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/story/${story.slug}`;

  const shareStory = async () => {
    const data = { title: `${story.title} — Regal Stories`, text: `A story from Mr. Regal: “${story.title}” — ${story.teaser}`, url: shareUrl };
    if (navigator.share) {
      try {
        await navigator.share(data);
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Story link copied — paste it anywhere, including Instagram");
    } catch (e) {
      toast(shareUrl);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 min-h-[72vh]" data-testid="story-player">
      <Seo title={`${story.title} — Regal Stories`} description={story.teaser} path={`/stories/${story.slug}`} />
      <audio ref={audioRef} src={`/audio/stories/${story.slug}.mp3`} preload="metadata" onTimeUpdate={onTime} onEnded={onEnded} />
      <audio ref={ambientRef} src="/audio/stories/ambient.wav" preload="none" loop />

      <Link to="/stories" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#7a7160] hover:text-[#a8842c] transition-colors" data-testid="story-back">
        <ArrowLeft size={12} /> All Stories
      </Link>

      <div className="mt-6 lg:flex lg:items-start lg:gap-10">
        <img src={`/mr-regal-${pose}.webp`} alt="" aria-hidden="true" loading="lazy" decoding="async" className="hidden lg:block h-64 w-auto shrink-0 opacity-95" />
        <div className="flex-1">
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#a8842c]">{story.collectionTitle}</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#171310] mt-2" data-testid="story-title">{story.title}</h1>
          <p className="text-sm text-[#8f8571] mt-2 italic">{story.teaser}</p>

          {/* Subtitles */}
          <div className="mt-8 min-h-[110px] border-l-2 border-[#c9a24b] pl-5" aria-live="polite">
            {ended ? (
              <div>
                <p className="font-heading text-lg text-[#8a6a1f]" data-testid="story-finished">The story is complete. Play it again, or let Mr. Regal tell you another.</p>
                {countdown !== null && (
                  <p className="text-sm text-[#8f8571] mt-2" data-testid="story-autoplay-countdown">
                    Next: “{next.title}” in {countdown}…
                  </p>
                )}
              </div>
            ) : subtitle ? (
              <p key={segIdx} className="font-heading text-lg sm:text-xl text-[#2b261e] leading-relaxed" data-testid="story-subtitle">{subtitle}</p>
            ) : (
              <p className="text-sm text-[#8f8571]" data-testid="story-hint">
                Press play — subtitles follow every spoken line.{!soundOn && " Audio is muted; tap Sound On to hear Mr. Regal."}
              </p>
            )}
          </div>

          {/* Progress */}
          <div className="h-px bg-[#eee6d6] mt-8 relative overflow-hidden" aria-hidden="true">
            <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#c9a24b] to-[#a8842c]" style={{ width: `${progress}%`, transition: "width 0.3s linear" }} />
          </div>

          {/* Controls */}
          <div className="flex flex-wrap items-center gap-2 mt-6">
            {ended ? (
              <button onClick={replay} className="rw-story-btn" data-testid="story-replay-btn"><RotateCcw size={13} /> Play Again</button>
            ) : (
              <button onClick={togglePlay} className="rw-story-btn rw-story-btn-primary" data-testid="story-play-btn">
                {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pause" : "Play Story"}
              </button>
            )}
            <button onClick={toggleSound} aria-pressed={soundOn} className="rw-story-btn" data-testid="story-sound-btn">
              {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />} Sound {soundOn ? "On" : "Off"}
            </button>
            <button onClick={toggleAutoplay} aria-pressed={autoplay} className="rw-story-btn" data-testid="story-autoplay-btn">
              <ListMusic size={13} /> Autoplay {autoplay ? "On" : "Off"}
            </button>
            <Link to={`/stories/${next.slug}`} className="rw-story-btn" data-testid="story-skip-btn">
              <SkipForward size={13} /> Skip to Next
            </Link>
          </div>

          {/* Share */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-5">
            <span className="text-[11px] tracking-[0.3em] uppercase text-[#b1a685]">Share this story</span>
            <a
              href={waShareLink(`A story from Mr. Regal ✨\n“${story.title}” — ${story.teaser}\n${shareUrl}`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#1f7a43] hover:text-[#a8842c] border-b border-[#bcd8c5] hover:border-[#a8842c] pb-0.5 transition-colors duration-300"
              data-testid="story-share-whatsapp"
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
            <button
              onClick={shareStory}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] uppercase text-[#8a6a1f] hover:text-[#a8842c] border-b border-[#c9a24b] hover:border-[#a8842c] pb-0.5 transition-colors duration-300"
              data-testid="story-share-btn"
            >
              <Share2 size={12} /> Instagram & more
            </button>
          </div>

          {/* Next up */}
          <div className="mt-10 border-t border-[#eee6d6] pt-5">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#b1a685]">Next Story</p>
            <Link to={`/stories/${next.slug}`} className="font-heading text-lg text-[#171310] hover:text-[#8a6a1f] transition-colors" data-testid="story-next">
              {next.title}
            </Link>
            <p className="text-xs text-[#8f8571] mt-1">{next.teaser}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
